import React, { useEffect, useMemo, useState } from 'react';
import { Wallet } from 'ethers';
import { autoMintSingleSideLp } from './lpAutoMint';
import { estimatePositionUsd, fetchOnchainV3Positions } from './lpOnchainPositions';

const BSC_CHAIN_ID = '0x38';
const STORAGE_WALLET = 'predict_lp_bsc_owner_v1';

function numberOr(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatUsd(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function formatApr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n >= 10) return `${(n * 100).toFixed(0)}%`;
  if (n >= 1) return `${(n * 100).toFixed(1)}%`;
  return `${(n * 100).toFixed(2)}%`;
}

function formatMoney(value, digits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1) return `$${n.toFixed(2)}`;
  if (Math.abs(n) >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

function formatPct(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${(n * 100).toFixed(digits)}%`;
}

function formatAmt(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '0';
  if (n >= 1000) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  return n.toPrecision(4);
}

function impermanentLoss(priceMove) {
  const k = 1 + Number(priceMove);
  if (!(k > 0)) return null;
  return (2 * Math.sqrt(k)) / (1 + k) - 1;
}

function yieldOnCapital(feeApr, capital, hours) {
  const apr = Number(feeApr);
  const cap = Number(capital);
  if (!Number.isFinite(apr) || !Number.isFinite(cap) || cap <= 0) return null;
  return cap * apr / 365 / 24 * hours;
}

function loadOwner() {
  try {
    return String(localStorage.getItem(STORAGE_WALLET) || '').trim();
  } catch {
    return '';
  }
}

function saveOwner(addr) {
  try {
    localStorage.setItem(STORAGE_WALLET, String(addr || ''));
  } catch (_) {}
}

function feeTier(pair) {
  const pct = Number(pair?.feeRatePct);
  if (!Number.isFinite(pct) || pct <= 0) return 2500;
  return Math.round(pct * 10000);
}

function buildSingleSideAddUrl(pair, side) {
  if (!pair) return '';
  const token = pair.tokenAddress;
  const quote = pair.quoteAddress;
  const fee = feeTier(pair);
  const isUni = (pair.dexBrand?.id || pair.dexId || '').toLowerCase().includes('uniswap');
  if (isUni) {
    const a = side === 'quote' ? quote : token;
    const b = side === 'quote' ? token : quote;
    return `https://app.uniswap.org/positions/create?currencyA=${a}&currencyB=${b}&fee=${fee}&chain=bnb`;
  }
  return `https://pancakeswap.finance/add/${token}/${quote}/${fee}`;
}

async function ensureBscNetwork() {
  const eth = window.ethereum;
  if (!eth?.request) return { ok: false, message: '未检测到钱包，将直接打开 DEX 页面，请自行切换到 BSC。' };
  try {
    await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BSC_CHAIN_ID }] });
    return { ok: true };
  } catch (err) {
    if (err?.code === 4902) {
      try {
        await eth.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: BSC_CHAIN_ID,
            chainName: 'BNB Smart Chain',
            nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
            rpcUrls: ['https://bsc-dataseed.binance.org/'],
            blockExplorerUrls: ['https://bscscan.com'],
          }],
        });
        return { ok: true };
      } catch (addErr) {
        return { ok: false, message: addErr?.message || '添加 BSC 网络失败' };
      }
    }
    if (err?.code === 4001) return { ok: false, message: '已取消切换网络' };
    return { ok: false, message: err?.message || '切换 BSC 失败，请手动切换后继续' };
  }
}

function calcRange(entryPrice, side, widthPct) {
  const w = Math.max(0.01, Number(widthPct) / 100);
  const p = Number(entryPrice);
  if (!(p > 0)) return { low: null, high: null };
  if (side === 'quote') return { low: p * (1 - w * 1.2), high: p * (1 - w * 0.15) };
  return { low: p * (1 + w * 0.15), high: p * (1 + w * 1.2) };
}

export default function LpAdvancedTrade({
  pools = [],
  activePair = null,
  tokenSymbol = 'TOKEN',
  tokenAddress = '',
  defaultCapital = 1000,
  onSelectPair,
}) {
  const v3Pools = useMemo(
    () => (pools || []).filter((item) => String(item.version || '').toLowerCase() === 'v3'),
    [pools],
  );

  const [pairAddress, setPairAddress] = useState('');
  const [side, setSide] = useState('quote');
  const [widthPct, setWidthPct] = useState(10);
  const [capital, setCapital] = useState(String(defaultCapital || 1000));
  const [owner, setOwner] = useState(() => loadOwner());
  const [onchainPositions, setOnchainPositions] = useState([]);
  const [posLoading, setPosLoading] = useState(false);
  const [posSyncedAt, setPosSyncedAt] = useState(0);
  const [onlyCurrentToken, setOnlyCurrentToken] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [privateKey, setPrivateKey] = useState('');
  const [riskAck, setRiskAck] = useState(false);
  const [autoLogs, setAutoLogs] = useState([]);
  const [lastTx, setLastTx] = useState(null);

  useEffect(() => {
    if (activePair?.version === 'v3' && activePair?.pairAddress) {
      setPairAddress(activePair.pairAddress);
    } else if (!pairAddress && v3Pools[0]?.pairAddress) {
      setPairAddress(v3Pools[0].pairAddress);
    }
  }, [activePair?.pairAddress, activePair?.version, v3Pools, pairAddress]);

  useEffect(() => {
    setCapital(String(defaultCapital || 1000));
  }, [defaultCapital]);

  const selected = useMemo(
    () => v3Pools.find((item) => item.pairAddress === pairAddress) || v3Pools[0] || null,
    [v3Pools, pairAddress],
  );

  const entryPrice = Number(selected?.priceUsd);
  const range = calcRange(entryPrice, side, widthPct);
  const cap = Math.max(0, numberOr(capital, 0));
  const preview1h = yieldOnCapital(selected?.feeApr, cap, 1);
  const previewDay = yieldOnCapital(selected?.feeApr, cap, 24);
  const previewIlSamples = [
    { label: '相对入场 -10%', move: -0.1 },
    { label: '相对入场 +10%', move: 0.1 },
    { label: '相对入场 -20%', move: -0.2 },
    { label: '相对入场 +20%', move: 0.2 },
  ];

  const priceMap = useMemo(() => {
    const map = {};
    for (const pool of pools || []) {
      const token = String(pool.tokenAddress || '').toLowerCase();
      const quote = String(pool.quoteAddress || '').toLowerCase();
      if (token && Number(pool.priceUsd) > 0) map[token] = Number(pool.priceUsd);
      const qSym = String(pool.quoteSymbol || '').toUpperCase();
      if (quote && ['USDT', 'USDC', 'USD1', 'BUSD', 'DAI'].includes(qSym)) map[quote] = 1;
    }
    return map;
  }, [pools]);

  const displayPositions = useMemo(
    () => onchainPositions.map((pos) => ({ ...pos, usd: estimatePositionUsd(pos, priceMap) })),
    [onchainPositions, priceMap],
  );

  async function loadOnchainPositions(addressOverride) {
    const addr = String(addressOverride || owner || '').trim();
    if (!/^0x[a-fA-F0-9]{40}$/i.test(addr)) {
      setActionMsg('请填写有效的 BSC 钱包地址以读取 Uniswap / Pancake 仓位');
      return;
    }
    setPosLoading(true);
    setActionMsg('');
    try {
      saveOwner(addr);
      setOwner(addr);
      const result = await fetchOnchainV3Positions({
        owner: addr,
        filterToken: onlyCurrentToken ? (tokenAddress || activePair?.tokenAddress || '') : '',
      });
      setOnchainPositions(result.positions || []);
      setPosSyncedAt(result.syncedAt || Date.now());
      setActionMsg(`已读取链上仓位 ${result.positions?.length || 0} 个（Pancake + Uniswap V3）`);
    } catch (err) {
      setActionMsg(err?.message || '读取链上仓位失败');
    } finally {
      setPosLoading(false);
    }
  }

  useEffect(() => {
    if (!/^0x[a-fA-F0-9]{40}$/i.test(owner || '')) return undefined;
    loadOnchainPositions(owner);
    const timer = window.setInterval(() => loadOnchainPositions(owner), 30000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, onlyCurrentToken, tokenAddress]);

  async function oneClickOpen() {
    if (!selected) {
      setActionMsg('请先选择一个 V3 可单边池子');
      return;
    }
    setBusy(true);
    setActionMsg('');
    try {
      const net = await ensureBscNetwork();
      if (!net.ok && net.message) setActionMsg(net.message);
      window.open(buildSingleSideAddUrl(selected, side), '_blank', 'noopener,noreferrer');
      onSelectPair?.(selected.pairAddress);
      setActionMsg((prev) => prev || `已打开 ${selected.dexBrand?.name || selected.dexId} 组池页。`);
    } finally {
      setBusy(false);
    }
  }

  async function runAutoMint() {
    if (!selected) {
      setActionMsg('请先选择一个 V3 池');
      return;
    }
    if (!riskAck) {
      setActionMsg('请先勾选风险确认');
      return;
    }
    if (!privateKey.trim()) {
      setActionMsg('请填写用于自动组池的私钥（建议专用小额钱包）');
      return;
    }
    setBusy(true);
    setActionMsg('');
    setLastTx(null);
    setAutoLogs([]);
    try {
      let walletAddr = '';
      try {
        walletAddr = new Wallet(privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`).address;
      } catch (_) {}
      const result = await autoMintSingleSideLp({
        privateKey,
        pair: selected,
        side,
        capitalUsd: cap,
        widthPct: Number(widthPct),
        onProgress: (step, detail) => {
          setAutoLogs((prev) => [...prev.slice(-20), { t: Date.now(), step, detail: String(detail || '') }]);
        },
      });
      setLastTx(result);
      setPrivateKey('');
      if (walletAddr) {
        setOwner(walletAddr);
        saveOwner(walletAddr);
        await loadOnchainPositions(walletAddr);
      }
      setActionMsg(`自动组池成功，已刷新链上仓位。Tx: ${result.txHash}`);
      onSelectPair?.(selected.pairAddress);
    } catch (err) {
      setActionMsg(err?.shortMessage || err?.reason || err?.message || '自动组池失败');
    } finally {
      setBusy(false);
    }
  }

  const syncedText = posSyncedAt
    ? new Date(posSyncedAt).toLocaleTimeString('zh-CN', { hour12: false })
    : '-';

  return (
    <section className="lp-arb-advanced">
      <div className="lp-arb-section-head">
        <h2>进阶交易 · 一键单边组池</h2>
        <p>
          可打开 DEX 手动组池，或本地私钥自动 Approve + Mint。下方<strong>直接读取 Uniswap / Pancake 链上仓位</strong>
          的本金市值与可领手续费（不再本地纸面监控）。
        </p>
      </div>

      <div className="lp-adv-layout">
        <div className="lp-adv-form">
          <label>
            目标 V3 池
            <select
              value={selected?.pairAddress || ''}
              onChange={(event) => {
                setPairAddress(event.target.value);
                onSelectPair?.(event.target.value);
              }}
            >
              {!v3Pools.length && <option value="">暂无 V3 可单边池</option>}
              {v3Pools.map((item) => (
                <option key={item.pairAddress} value={item.pairAddress}>
                  {item.pairLabel} · {item.dexBrand?.name || item.dexId} · {Number(item.feeRatePct).toFixed(2)}% · APR {formatApr(item.feeApr)}
                </option>
              ))}
            </select>
          </label>

          <div className="lp-adv-side">
            <button type="button" className={side === 'quote' ? 'active' : ''} onClick={() => setSide('quote')}>
              <b>单边报价币</b>
              <span>下跌买币 · 区间在现价下方</span>
            </button>
            <button type="button" className={side === 'token' ? 'active' : ''} onClick={() => setSide('token')}>
              <b>单边目标币</b>
              <span>上涨卖币 · 区间在现价上方</span>
            </button>
          </div>

          <div className="lp-adv-inputs">
            <label>
              本金（USD）
              <input value={capital} onChange={(event) => setCapital(event.target.value)} type="number" min="1" step="50" />
            </label>
            <label>
              区间宽度（%）
              <input
                value={widthPct}
                onChange={(event) => setWidthPct(Math.max(1, Math.min(80, Number(event.target.value) || 10)))}
                type="number"
                min="1"
                max="80"
                step="1"
              />
            </label>
          </div>

          <div className="lp-adv-range">
            <div><span>参考现价</span><strong>{formatMoney(entryPrice, 6)}</strong></div>
            <div><span>建议区间低</span><strong>{formatMoney(range.low, 6)}</strong></div>
            <div><span>建议区间高</span><strong>{formatMoney(range.high, 6)}</strong></div>
            <div><span>单边资产</span><strong>{side === 'quote' ? (selected?.quoteSymbol || 'USDT') : (selected?.tokenSymbol || tokenSymbol)}</strong></div>
          </div>

          <div className="lp-adv-actions">
            <button type="button" className="primary" disabled={busy || !selected} onClick={oneClickOpen}>
              {busy ? '处理中…' : '打开 DEX 手动确认组池'}
            </button>
          </div>
          {actionMsg && <div className="lp-adv-msg">{actionMsg}</div>}

          <div className="lp-adv-auto">
            <h3>私钥自动组池（无需手动点 DEX）</h3>
            <p className="lp-adv-auto-warn">
              私钥只在浏览器内存签名，<b>不会上传服务器</b>。请只用<strong>专用小额钱包</strong>。成功后自动刷新该地址链上仓位。
            </p>
            <label>
              钱包私钥（用完清空）
              <input
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="0x… 或 64 位十六进制"
                value={privateKey}
                onChange={(event) => setPrivateKey(event.target.value)}
                disabled={busy}
              />
            </label>
            <label className="lp-adv-ack">
              <input type="checkbox" checked={riskAck} onChange={(event) => setRiskAck(event.target.checked)} disabled={busy} />
              我已知晓风险，仅用小额测试钱包，并确认本金/方向/区间无误
            </label>
            <div className="lp-adv-actions">
              <button type="button" className="danger" disabled={busy || !selected || !riskAck} onClick={runAutoMint}>
                {busy ? '链上执行中…' : '私钥自动单边组池'}
              </button>
            </div>
            {!!autoLogs.length && (
              <div className="lp-adv-logs">
                {autoLogs.map((item, index) => (
                  <div key={`${item.t}-${index}`}><b>{item.step}</b><span>{item.detail}</span></div>
                ))}
              </div>
            )}
            {lastTx?.explorer && (
              <a className="lp-adv-tx" href={lastTx.explorer} target="_blank" rel="noreferrer">在 BscScan 查看交易</a>
            )}
          </div>
        </div>

        <div className="lp-adv-live">
          <h3>开仓前预览</h3>
          <div className="lp-adv-metrics">
            <article><span>计划本金</span><strong>{formatUsd(cap)}</strong><small>用于开仓参考</small></article>
            <article><span>约 1h 收益</span><strong>{formatMoney(preview1h, 4)}</strong><small>入区间后按 APR 估算</small></article>
            <article><span>约 1 日收益</span><strong>{formatMoney(previewDay, 4)}</strong><small>年化 {formatApr(selected?.feeApr)}</small></article>
            <article><span>等待期 IL</span><strong>$0.00</strong><small>未进区间近似无换仓 IL</small></article>
          </div>
          <div className="lp-adv-il-mini">
            <b>入区间后无偿损失情景（估算）</b>
            <div className="lp-adv-il-grid">
              {previewIlSamples.map((item) => {
                const il = impermanentLoss(item.move);
                return (
                  <div key={item.label}>
                    <span>{item.label}</span>
                    <strong className={il < -0.02 ? 'danger' : ''}>{formatPct(il, 2)}</strong>
                    <small>{formatMoney((il || 0) * cap, 2)}</small>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="lp-adv-positions">
        <div className="lp-arb-section-head">
          <h2>链上仓位（Uniswap / Pancake）</h2>
          <p>读取 PositionManager NFT：本金市值、可领手续费、是否在区间。约 30 秒自动刷新。</p>
        </div>

        <div className="lp-adv-owner-bar">
          <label>
            钱包地址
            <input value={owner} onChange={(event) => setOwner(event.target.value.trim())} placeholder="0x… BSC 地址" />
          </label>
          <label className="lp-adv-ack">
            <input type="checkbox" checked={onlyCurrentToken} onChange={(event) => setOnlyCurrentToken(event.target.checked)} />
            只看当前代币相关仓位
          </label>
          <button type="button" className="primary" disabled={posLoading} onClick={() => loadOnchainPositions()}>
            {posLoading ? '读取中…' : '读取链上仓位'}
          </button>
          <small>最后同步 {syncedText}</small>
        </div>

        {!displayPositions.length && !posLoading && (
          <div className="lp-arb-empty">暂无链上仓位。填写地址后点「读取链上仓位」，或先自动组池成功后再刷新。</div>
        )}

        <div className="lp-adv-pos-grid">
          {displayPositions.map((pos) => (
            <article
              key={pos.id}
              className={`lp-adv-pos-card status-${pos.status === 'earning' ? 'earning' : pos.status === 'below' ? 'waiting' : 'out'}`}
            >
              <header>
                <div>
                  <b>{pos.pairLabel}</b>
                  <span>{pos.dexName} V3 · NFT #{pos.tokenId} · 费率 {pos.feePct}%</span>
                </div>
                <em>{pos.statusText}</em>
              </header>
              <div className="lp-adv-pos-metrics">
                <div>
                  <span>仓位本金（市值）</span>
                  <strong>{formatUsd(pos.usd.principalUsd)}</strong>
                </div>
                <div>
                  <span>可领手续费</span>
                  <strong className="up">{formatUsd(pos.usd.feesUsd)}</strong>
                </div>
                <div>
                  <span>合计</span>
                  <strong>{formatUsd(pos.usd.totalUsd)}</strong>
                </div>
                <div>
                  <span>持仓数量</span>
                  <strong>{formatAmt(pos.amount0Human)} {pos.symbol0}<br />{formatAmt(pos.amount1Human)} {pos.symbol1}</strong>
                </div>
                <div>
                  <span>可领数量</span>
                  <strong>{formatAmt(pos.fee0Human)} {pos.symbol0}<br />{formatAmt(pos.fee1Human)} {pos.symbol1}</strong>
                </div>
                <div>
                  <span>当前 tick</span>
                  <strong>{pos.tickCurrent}</strong>
                </div>
              </div>
              <footer>
                <small>区间 tick {pos.tickLower} ~ {pos.tickUpper}{pos.inRange ? ' · 在区间' : ' · 出区间'}</small>
                <div>
                  <a
                    href={pos.dex === 'uniswap'
                      ? `https://app.uniswap.org/positions/v3/bnb/${pos.tokenId}`
                      : `https://pancakeswap.finance/liquidity/${pos.tokenId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    打开仓位
                  </a>
                  <a href={`https://bscscan.com/address/${pos.pool}`} target="_blank" rel="noreferrer">池子</a>
                </div>
              </footer>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
